import React, {
    useEffect,
    useContext,
    useState,
    useRef
} from 'react';
import { v4 as uuid } from 'uuid';

import withAuth from '@hocs/withAuth';
import MainLayout, { HeaderContext } from '@lib/layout/main';
import deepEqual from 'deep-equal';

// Material ui
import {
    Grid,
    Menu,
    MenuItem
} from '@material-ui/core';

// Components
import AccountsHeader from '@components/Accounts/Header';
import AccountsHero from '@components/Accounts/Hero';
import Typography from '@components/UI-KIT/Typography';
import MyAccountsSection from '@components/Accounts/MyAccountsSection';
import { SnackbarContext } from '@/src/lib/layout/main';
import SideDrawer from '@components/UI-KIT/SideDrawer';
import AccountFormStepper from '@components/Accounts/AccountFormStepper';
import TransactionsTable from '@components/Accounts/TransactionsTable';
import Button from '@components/UI-KIT/Button';
import Modal from '@components/UI-KIT/Modal';
import EmptyState from '@components/EmptyState';
import TransactionFormStepper from '@components/Accounts/TransactionFormStepper';

// Services
import {
    getTotalAmountFromAccounts,
    getTotalAmountFromPropietorAccounts,
    fetchAccounts,
    Account,
    deleteAccount
} from '@services/account';
import {
    TransactionsFilters,
    TransactionsFiltersDefault,
    fetchAccountTransactions,
    patchAccountStatus
} from '@services/account-transaction';

// Icons
import { SnackbarSuccess, SnackbarError } from '@/assets/icons';

let timeout: NodeJS.Timeout;

const AccountsPage: React.FC = () => {
    const [ heroData, setHeroData ] = useState({
        totalAmountInAccounts: 0,
        totalPropietorsAmount: 0,
        totalAdministrationsAmount: 0
    });
    const [
        {
            drawerOpened,
            drawerTitle,
            drawerChildren,
            drawerFooter,
            titleIcon,
            drawerClass
        },
        setDrawer
    ] = useState({
        drawerOpened: false,
        drawerTitle: '',
        titleIcon: null,
        drawerChildren: null,
        drawerFooter: true,
        drawerClass: ''
    });
    const [ organizationAccounts, setOrganizationAccounts ] = useState<Account[]>([]);
    const [ transactionStatus, setTransactionStatus ] = useState<'pending' | 'ready'>('pending');
    const [ accountStatus, setAccountStatus ] = useState<'pending' | 'ready'>('pending');
    const [ buttonDisabled, setButtonDisabled ] = useState(false);

    const [ modal, setModal ] = useState({
        text: '',
        show: false,
        snackbar: {
            success: '',
            error: ''
        },
        action: () => null
    });

    const confirmDeleteAccount = async () => {
        setButtonDisabled(true);

        const ok = await modal.action();
        closeModal();
        if (!ok) {
            snackbarContext.setSnackbar({
                snackbarMessage: modal.snackbar.error,
                snackbarIcon: (
                    <SnackbarError />
                ),
                snackbarColor: 'error'
            });
            return;
        }

        snackbarContext.setSnackbar({
            snackbarMessage: modal.snackbar.success,
            snackbarIcon: (
                <SnackbarSuccess />
            ),
            snackbarColor: 'success'
        });

        setButtonDisabled(false);
    };

    const [ transactionsTable, setTransactionsTable ] = useState({
        page: 1,
        size: 50
    });
    const [ accountTransactions, setAccountTransactions ] = useState({
        items: [],
        totalItems: 0
    });
    const [ searchTransaction, setSearchTransaction ] = useState<string>('');
    const [ filters, setFilters ] = useState(TransactionsFiltersDefault());
    const [ menu, setMenu ] = useState({
        account: null,
        anchorEl: null
    });

    const lastAppliedFilters = useRef<TransactionsFilters>(TransactionsFiltersDefault());

    const snackbarContext = useContext(SnackbarContext);
    const headerContext = useContext(HeaderContext);

    const handleFetchHeroData = async () => {
        const { data: { totalAmount: totalAmountInAccounts }} = await getTotalAmountFromAccounts();
        const { data: { totalAmount: totalPropietorsAmount }} = await getTotalAmountFromPropietorAccounts();
        setHeroData({
            totalAmountInAccounts,
            totalPropietorsAmount,
            totalAdministrationsAmount: totalAmountInAccounts - totalPropietorsAmount
        });
    };

    const handleFetchOrganizationAccounts = async () => {
        setAccountStatus('pending');
        const { data: { items }} = await fetchAccounts(0, 0);
        const accounts: Account[] = [ ...items ];
        accounts.sort(a => a.isDefault ? -1 : 1);
        setOrganizationAccounts(accounts);
        setAccountStatus('ready');
    };

    const deleteOrganizationAccounts = async (id: number) => {
        const { status } = await deleteAccount(id)
            .catch(() => {
                snackbarContext.setSnackbar({
                    snackbarMessage: 'No se pudo eliminar la cuenta',
                    snackbarColor: 'error',
                    snackbarIcon: (
                        <SnackbarError />
                    )
                });
                return { status: 400 };
            });
        status === 200
            ? snackbarContext.setSnackbar({
                snackbarMessage: 'La cuenta ha sido eliminada',
                snackbarColor: 'success',
                snackbarIcon: (
                    <SnackbarSuccess />
                )
            })
            : snackbarContext.setSnackbar({
                snackbarMessage: 'No se pudo eliminar la cuenta',
                snackbarColor: 'error',
                snackbarIcon: (
                    <SnackbarError />
                )
            });
        await handleFetchOrganizationAccounts();
        await handleFetchHeroData();
    };

    const markAccountAsDefault = async (id: number) => {
        const { status } = await patchAccountStatus(id, true)
            .catch(() => {
                snackbarContext.setSnackbar({
                    snackbarMessage: 'No se pudo cambiar la cuenta principal',
                    snackbarColor: 'error',
                    snackbarIcon: (
                        <SnackbarError />
                    )
                });
                return { status: 400 };
            });
        status === 200
            ? snackbarContext.setSnackbar({
                snackbarMessage: 'Cambiaste tu cuenta principal',
                snackbarColor: 'success',
                snackbarIcon: (
                    <SnackbarSuccess />
                )
            })
            : snackbarContext.setSnackbar({
                snackbarMessage: 'No se pudo cambiar la cuenta principal',
                snackbarColor: 'error',
                snackbarIcon: (
                    <SnackbarError />
                )
            });
        await handleFetchOrganizationAccounts();
    };

    const handleFetchAccountTransactions = () => {
        if (timeout) clearTimeout(timeout); // this will prevent request from being triggered twice when changing more than one query param
        timeout = setTimeout(async () => {
            setTransactionStatus('pending');
            const { data: { items, totalItems }} = await fetchAccountTransactions(
                transactionsTable.page,
                transactionsTable.size,
                searchTransaction,
                filters
            );
            setAccountTransactions({
                items,
                totalItems
            });
            setTransactionStatus('ready');
            lastAppliedFilters.current = filters;
        }, 300);
    };

    useEffect(() => {
        handleFetchHeroData();
        handleFetchOrganizationAccounts();
    }, [ ]);

    useEffect(() => {
        handleFetchAccountTransactions();
    }, [ transactionsTable, searchTransaction ]);

    const filtersApplied = !deepEqual(lastAppliedFilters.current, TransactionsFiltersDefault());

    useEffect(() => {
        headerContext.setHeaderChildren(
            <AccountsHeader
                setDrawer={setDrawer}
                handleFetchHeroData={handleFetchHeroData}
                handleFetchOrganizationAccounts={handleFetchOrganizationAccounts}
                handleFetchAccountTransactions={handleFetchAccountTransactions}
            />
        );
        headerContext.setHero(
            <AccountsHero
                heroData={heroData}
            />
        );
    }, [ heroData ]);

    const closeModal = () => setModal({
        text: '',
        show: false,
        snackbar: {
            success: '',
            error: ''
        },
        action: () => null
    });

    const actions = (account: Account) => ([
        {
            label: 'Marcar como principal',
            disabled: account?.isDefault,
            handler: (() => markAccountAsDefault(account.id))
        },
        {
            label: 'Editar cuenta',
            handler: (() => setDrawer({
                drawerOpened: true,
                drawerTitle: 'Editar cuenta',
                titleIcon: null,
                drawerChildren: (
                    <AccountFormStepper
                        setDrawer={setDrawer}
                        handleFetchHeroData={handleFetchHeroData}
                        handleFetchOrganizationAccounts={handleFetchOrganizationAccounts}
                        account={account}
                    />
                ),
                drawerFooter: true,
                drawerClass: ''
            }))
        },
        {
            label: (
                <span
                    className="AOL-my-account-section-delete-account"
                >
                    Eliminar cuenta
                </span>
            ),
            disabled: account?.isDefault || account?.amount !== 0,
            handler: () => setModal({
                show: true,
                text: '¿Deseas eliminar la cuenta? Esta acción no se puede deshacer.',
                snackbar: {
                    success: 'La cuenta fue eliminada',
                    error: 'No se pudo eliminar la cuenta'
                },
                action: () => deleteOrganizationAccounts(account.id)
            })
        }
    ]);

    return (
        <>
            <Grid
                container
            >
                <MyAccountsSection
                    setMenu={setMenu}
                    accountStatus={accountStatus}
                    accounts={organizationAccounts}
                />
                <Menu
                    id={`simple-menu`}
                    keepMounted
                    className="mui-fixed"
                    style={{ zIndex: 3200 }}
                    classes={{
                        list: 'ui-Card-item-context-menu',
                        paper: 'ui-Card-item-menu-shadow-controller'
                    }}
                    key={`id-simple-menu${menu.account ? menu.account.id : ''}`}
                    anchorEl={menu.anchorEl}
                    open={Boolean(menu.anchorEl)}
                    onClose={() => setMenu({
                        anchorEl: null,
                        account: null
                    })}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem
                        onClick={() => setMenu({
                            anchorEl: null,
                            account: null
                        })}
                        style={{ display: 'none' }}
                    />
                    { actions(menu.account).map(action => (
                        <MenuItem
                            key={uuid()}
                            className="ui-Card-item-menu-item"
                            disabled={action.disabled}
                            onClick={() => {
                                setMenu({
                                    anchorEl: null,
                                    account: null
                                });
                                action.handler();
                            }}
                        >
                            {action.label}
                        </MenuItem>
                    ))}
                </Menu>
                <TransactionsTable
                    accounts={organizationAccounts}
                    filtersApplied={filtersApplied}
                    transactions={accountTransactions}
                    snackbarContext={snackbarContext}
                    table={transactionsTable}
                    setTable={setTransactionsTable}
                    filters={filters}
                    setFilters={setFilters}
                    transactionStatus={transactionStatus}
                    setTransactionStatus={setTransactionStatus}
                    handleFetchAccountTransactions={handleFetchAccountTransactions}
                    handleFetchOrganizationAccounts={handleFetchOrganizationAccounts}
                    handleFetchHeroData={handleFetchHeroData}
                    search={searchTransaction}
                    setModal={setModal}
                    setSearch={setSearchTransaction}
                    setDrawer={setDrawer}
                    title="Mis transacciones"
                    emptyState={(
                        <EmptyState
                            title="Registra tu primera transacción"
                            imagePath="/images/EmptyState/EmptyAccount.svg"
                            buttons={[
                                {
                                    buttonLabel: 'Nueva transacción',
                                    iconName: 'Write',
                                    subtitle: 'Cuando hagas transacciones tales como ingresos, egresos o movimientos entre cuentas, podrás verlas acá. ¿Qué tal si registras tu primera transacción?',
                                    action: () => setDrawer({
                                        drawerOpened: true,
                                        drawerTitle: 'Nueva transacción',
                                        titleIcon: null,
                                        drawerChildren: (
                                            <TransactionFormStepper
                                                setDrawer={setDrawer}
                                                handleFetchHeroData={handleFetchHeroData}
                                                handleFetchOrganizationAccounts={handleFetchOrganizationAccounts}
                                                handleFetchAccountTransactions={handleFetchAccountTransactions}
                                            />
                                        ),
                                        drawerFooter: true,
                                        drawerClass: ''
                                    })
                                }
                            ]}
                        />
                    )}
                />
            </Grid>
            <SideDrawer
                withFooter={drawerFooter}
                open={drawerOpened}
                titleIcon={titleIcon}
                className={drawerClass}
                closeDrawer={() => setDrawer({
                    drawerOpened: false,
                    drawerTitle: '',
                    drawerChildren: null,
                    drawerFooter: null,
                    titleIcon: null,
                    drawerClass: ''
                })}
                title={drawerTitle}
                children={drawerChildren}
            />
            { modal && (
                <Modal
                    title="Confirmar eliminación"
                    open={modal.show}
                    closeModal={closeModal}
                >
                    <Typography
                        color="primary-dark"
                    >
                        {modal.text}
                    </Typography>
                    <Grid
                        className="AOL-transactions-modal-buttons"
                        container
                        justify="space-between"
                    >
                        <Button
                            level={2}
                            label="Cancelar"
                            className="AOL-Modal-button"
                            onClick={closeModal}
                        />
                        <Button
                            level={1}
                            className="AOL-Modal-button"
                            label="Confirmar"
                            disabled={buttonDisabled}
                            onClick={confirmDeleteAccount}
                            loadingEvent
                        />
                    </Grid>
                </Modal>
            )}
        </>
    );
};

export default withAuth(MainLayout(AccountsPage));
